import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Star, HandHeart, ChatCircle, ArrowRight } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";

const STATUS = { active: "🟢", urgent: "🔴", partially_funded: "🟡", fully_funded: "🔵", completed: "⚪" };

export default function ChampionDashboard() {
  const [d, setD] = useState(null);
  useSeo("Champion Dashboard", "Manage the institution needs you champion.");
  useEffect(() => { api.get("/me/champion-dashboard").then((r) => setD(r.data)).catch(() => setD(false)); }, []);

  if (d === false) return <div className="mx-auto max-w-4xl px-4 py-20 text-center text-muted-foreground">Please log in.</div>;
  if (!d) return <div className="mx-auto max-w-4xl px-4 py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="champion-dashboard">
      <div className="flex items-center gap-2">
        <Star size={24} weight="duotone" className="text-secondary" />
        <h1 className="font-heading text-3xl font-medium">Champion Dashboard</h1>
      </div>
      <p className="mt-1 text-muted-foreground">Everything you're driving, in one place — stay active in two minutes.</p>

      <div className="mt-5 grid grid-cols-3 gap-3">
        {[
          { label: "Projects", value: d.counts.projects, icon: Star },
          { label: "Offers to nudge", value: d.counts.pending, icon: HandHeart },
          { label: "Unanswered", value: d.counts.unanswered, icon: ChatCircle },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border/60 bg-white p-4 text-center">
            <s.icon className="mx-auto text-secondary" size={20} weight="duotone" />
            <div className="mt-1 font-heading text-2xl font-medium">{s.value}</div>
            <div className="text-xs text-muted-foreground">{s.label}</div>
          </div>
        ))}
      </div>

      {d.projects.length === 0 && (
        <div className="mt-8 rounded-2xl border border-dashed border-border/60 p-8 text-center text-muted-foreground">
          You're not championing any needs yet. Find your institution and volunteer to champion a need.
          <div className="mt-3"><Link to="/explore/institutions" className="text-secondary hover:underline">Browse institutions →</Link></div>
        </div>
      )}

      {d.projects.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-xl font-medium">My projects</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {d.projects.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="block rounded-xl border border-border/60 bg-white p-4 hover:border-secondary/50" data-testid={`dash-project-${p.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate font-medium">{p.title}</span>
                  <span className="shrink-0 text-xs">{STATUS[p.status]}</span>
                </div>
                <div className="text-xs text-muted-foreground">{p.institution_name} · {p.supporter_count} supporters</div>
                {p.funding_goal > 0 && <Progress value={p.funded_pct} className="mt-2 h-1.5" />}
              </Link>
            ))}
          </div>
        </div>
      )}

      {d.pending_contributions.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-xl font-medium">Offers awaiting the institution</h2>
          <p className="text-xs text-muted-foreground">These alumni offered help — remind the institution admin to verify them.</p>
          <div className="mt-3 space-y-2">
            {d.pending_contributions.map((c) => (
              <Link key={c.id} to={`/projects/${c.project_id}`} className="flex items-center justify-between gap-2 rounded-xl border border-border/60 bg-white p-3 hover:border-secondary/50">
                <div className="min-w-0"><span className="font-medium">{c.anonymous ? "Anonymous" : c.user_name}</span> <span className="text-xs text-muted-foreground">· {c.help_label}{c.amount > 0 ? ` · $${c.amount.toLocaleString()}` : ""}</span></div>
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] capitalize text-amber-700">{c.status.replace("_", " ")}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {d.recent_comments.length > 0 && (
        <div className="mt-8">
          <h2 className="font-heading text-xl font-medium">Recent discussion</h2>
          <div className="mt-3 space-y-2">
            {d.recent_comments.map((c) => (
              <Link key={c.id} to={`/projects/${c.project_id}`} className="block rounded-xl border border-border/60 bg-white p-3 hover:border-secondary/50">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">{c.user_name}</span>
                  {c.unanswered && <span className="rounded-full bg-secondary/15 px-2 py-0.5 text-[10px] text-secondary">Needs a reply</span>}
                  <ArrowRight size={13} className="ml-auto text-muted-foreground" />
                </div>
                <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">{c.text} <span className="text-xs">· {c.project_title}</span></p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
